import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, MapPin, Heart, Users, Gift, 
  ChevronDown, X, Play, Image as ImageIcon, Filter,
  Clock, ChevronRight, Loader2
} from 'lucide-react';
import BrazilMap from './BrazilMap';
import '../MapaAcoes.css';
import '../BrazilMap.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Nomes dos meses em português
const monthNames = {
  '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março', '04': 'Abril',
  '05': 'Maio', '06': 'Junho', '07': 'Julho', '08': 'Agosto',
  '09': 'Setembro', '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro'
};

// Nomes dos estados
const stateNames = {
  AC: "Acre", AL: "Alagoas", AP: "Amapá", AM: "Amazonas", BA: "Bahia",
  CE: "Ceará", DF: "Distrito Federal", ES: "Espírito Santo", GO: "Goiás",
  MA: "Maranhão", MT: "Mato Grosso", MS: "Mato Grosso do Sul", MG: "Minas Gerais",
  PA: "Pará", PB: "Paraíba", PR: "Paraná", PE: "Pernambuco", PI: "Piauí",
  RJ: "Rio de Janeiro", RN: "Rio Grande do Norte", RS: "Rio Grande do Sul",
  RO: "Rondônia", RR: "Roraima", SC: "Santa Catarina", SP: "São Paulo",
  SE: "Sergipe", TO: "Tocantins"
};

const MapaAcoes = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedState, setSelectedState] = useState(searchParams.get('estado') || null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' ou 'map'
  const [selectedAction, setSelectedAction] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  
  // Estado para ações da API
  const [socialActions, setSocialActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Buscar ações da API
  useEffect(() => {
    const fetchActions = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/actions?status=publicado&limit=100`);
        if (response.ok) {
          const data = await response.json();
          if (data.actions) {
            setSocialActions(data.actions);
          }
        } else {
          setError('Erro ao carregar ações');
        }
      } catch (err) {
        console.error('Erro ao buscar ações:', err);
        setError('Erro ao conectar com o servidor');
      } finally {
        setLoading(false);
      }
    };
    fetchActions();
  }, []);

  // Atualizar URL quando estado selecionado muda
  useEffect(() => {
    if (selectedState) {
      setSearchParams({ estado: selectedState });
    } else {
      setSearchParams({});
    }
  }, [selectedState, setSearchParams]);

  // Organizar ações por mês
  const actionsByMonth = useMemo(() => {
    const grouped = {};
    socialActions.forEach(action => {
      if (action.month) {
        if (!grouped[action.month]) {
          grouped[action.month] = [];
        }
        grouped[action.month].push(action);
      }
    });
    
    // Ordenar por data (mais recente primeiro)
    const sorted = Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a))
      .reduce((acc, [key, val]) => {
        acc[key] = val;
        return acc;
      }, {});
    
    return sorted;
  }, [socialActions]);

  // Ações filtradas por estado
  const filteredActions = useMemo(() => {
    if (!selectedState) return socialActions;
    return socialActions.filter(action => action.state === selectedState);
  }, [selectedState, socialActions]);

  // Ações filtradas por mês
  const filteredByMonth = useMemo(() => {
    if (!selectedMonth) return filteredActions;
    return filteredActions.filter(action => action.month === selectedMonth);
  }, [filteredActions, selectedMonth]);

  // Lista de meses disponíveis
  const availableMonths = useMemo(() => {
    return Object.keys(actionsByMonth);
  }, [actionsByMonth]);

  // Formatar mês para exibição (sem ano)
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return monthNames[month];
  };

  // Lidar com clique no estado do mapa
  const handleStateClick = (stateCode) => {
    setSelectedState(stateCode);
    setViewMode('timeline');
  };

  // Limpar filtros
  const clearFilters = () => {
    setSelectedState(null);
    setSelectedMonth(null);
  };

  // Abrir modal de ação
  const openActionModal = (action) => {
    setSelectedAction(action);
    setCurrentMediaIndex(0);
  };

  return (
    <div className="mapa-acoes-page">
      {/* Header */}
      <header className="mapa-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h1>Nosso Impacto pelo Brasil</h1>
        <p>Acompanhe todas as nossas ações organizadas por localização e período</p>
      </header>

      {/* Navigation Tabs */}
      <div className="view-tabs">
        <button 
          className={`tab ${viewMode === 'map' ? 'active' : ''}`}
          onClick={() => setViewMode('map')}
        >
          <MapPin size={18} />
          <span>Mapa do Brasil</span>
        </button>
        <button 
          className={`tab ${viewMode === 'timeline' ? 'active' : ''}`}
          onClick={() => setViewMode('timeline')}
        >
          <Calendar size={18} />
          <span>Timeline</span>
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          {/* Filtro por Estado */}
          <div className="filter-group">
            <label>Estado</label>
            <div className="filter-select">
              <select 
                value={selectedState || ''} 
                onChange={(e) => setSelectedState(e.target.value || null)}
              >
                <option value="">Todos os estados</option>
                {Object.entries(stateNames)
                  .filter(([code]) => socialActions.some(a => a.state === code))
                  .map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))
                }
              </select>
              <ChevronDown size={16} />
            </div>
          </div>

          {/* Filtro por Mês */}
          <div className="filter-group">
            <label>Período</label>
            <div className="filter-select">
              <select 
                value={selectedMonth || ''} 
                onChange={(e) => setSelectedMonth(e.target.value || null)}
              >
                <option value="">Todos os períodos</option>
                {availableMonths.map(month => (
                  <option key={month} value={month}>{formatMonth(month)}</option>
                ))}
              </select>
              <ChevronDown size={16} />
            </div>
          </div>

          {/* Limpar Filtros */}
          {(selectedState || selectedMonth) && (
            <button className="clear-filters" onClick={clearFilters}>
              <X size={16} />
              Limpar filtros
            </button>
          )}
        </div>

        {/* Active Filters Tags */}
        {(selectedState || selectedMonth) && (
          <div className="active-filters">
            {selectedState && (
              <span className="filter-tag">
                <MapPin size={14} />
                {stateNames[selectedState]}
                <button onClick={() => setSelectedState(null)}><X size={12} /></button>
              </span>
            )}
            {selectedMonth && (
              <span className="filter-tag">
                <Calendar size={14} />
                {formatMonth(selectedMonth)}
                <button onClick={() => setSelectedMonth(null)}><X size={12} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="mapa-content">
        {/* Map View */}
        {viewMode === 'map' && (
          <section className="map-section">
            <div className="map-intro">
              <h2>Clique em um estado para ver as ações realizadas</h2>
              <p>Estados iluminados indicam onde já realizamos ações de caridade</p>
            </div>
            <BrazilMap onStateClick={handleStateClick} />
          </section>
        )}

        {/* Timeline View */}
        {viewMode === 'timeline' && (
          <section className="timeline-section">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
                <Loader2 size={48} style={{ color: '#d4af37', animation: 'spin 1s linear infinite' }} />
                <p style={{ color: '#888' }}>Carregando ações...</p>
              </div>
            ) : error ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
                <p style={{ color: '#ff6b6b' }}>{error}</p>
              </div>
            ) : socialActions.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
                <Gift size={48} style={{ color: '#888' }} />
                <p style={{ color: '#888' }}>Nenhuma ação cadastrada ainda</p>
              </div>
            ) : (
            <>
            {/* Stats Header */}
            <div className="timeline-stats">
              <div className="stat-card">
                <Heart className="stat-icon" />
                <div className="stat-info">
                  <span className="stat-value">{filteredByMonth.length}</span>
                  <span className="stat-label">Ações {selectedState ? `em ${stateNames[selectedState]}` : 'no total'}</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="timeline">
              {Object.entries(actionsByMonth)
                .filter(([month]) => !selectedMonth || month === selectedMonth)
                .map(([month, actions]) => {
                  const monthActions = actions.filter(action => 
                    !selectedState || action.state === selectedState
                  );
                  
                  if (monthActions.length === 0) return null;

                  return (
                    <div key={month} className="timeline-month">
                      <div className="month-header">
                        <div className="month-marker">
                          <Calendar size={20} />
                        </div>
                        <h3>{formatMonth(month)}</h3>
                        <span className="month-count">{monthActions.length} {monthActions.length === 1 ? 'ação' : 'ações'}</span>
                      </div>

                      <div className="month-actions">
                        {monthActions.map(action => (
                          <div 
                            key={action.id} 
                            className="action-card"
                            onClick={() => openActionModal(action)}
                          >
                            <div 
                              className="action-image"
                              style={{ 
                                backgroundImage: `url(${action.media[0]?.thumbnail || 'https://res.cloudinary.com/dnrowstdh/image/upload/v1774061579/vivaimpact_migrated/cyky6t4vjz1feewhh11b.jpg'})` 
                              }}
                            >
                              {action.media.some(m => m.type === 'video') && (
                                <div className="video-indicator">
                                  <Play size={20} />
                                </div>
                              )}
                              <div className="action-state-badge">
                                <MapPin size={12} />
                                {action.state}
                              </div>
                            </div>
                            <div className="action-content">
                              <h4>{action.title}</h4>
                              <p className="action-subtitle">{action.subtitle}</p>
                              <div className="action-meta">
                                <span><MapPin size={14} /> {action.location}</span>
                                <span><Users size={14} /> {action.beneficiaries}</span>
                              </div>
                              <div className="action-tags">
                                {action.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="tag">{tag}</span>
                                ))}
                              </div>
                            </div>
                            <ChevronRight className="action-arrow" size={20} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

              {/* Empty State */}
              {filteredByMonth.length === 0 && (
                <div className="empty-state">
                  <Gift size={48} />
                  <h3>Nenhuma ação encontrada</h3>
                  <p>Tente ajustar os filtros para ver mais resultados</p>
                  <button onClick={clearFilters}>Limpar filtros</button>
                </div>
              )}
            </div>
            </>
            )}
          </section>
        )}
      </main>

      {/* Action Modal */}
      {selectedAction && (
        <div className="action-modal-overlay" onClick={() => setSelectedAction(null)}>
          <div className="action-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedAction(null)}>
              <X size={24} />
            </button>

            {/* Media Gallery */}
            <div className="modal-media">
              {selectedAction.media[currentMediaIndex]?.type === 'video' ? (
                <iframe
                  src={selectedAction.media[currentMediaIndex].url}
                  title={selectedAction.title}
                  allowFullScreen
                  className="modal-video"
                />
              ) : (
                <img 
                  src={selectedAction.media[currentMediaIndex]?.url || selectedAction.media[currentMediaIndex]?.thumbnail} 
                  alt={selectedAction.title}
                  className="modal-image"
                />
              )}

              {/* Media Navigation */}
              {selectedAction.media.length > 1 && (
                <div className="media-nav">
                  {selectedAction.media.map((media, idx) => (
                    <button
                      key={idx}
                      className={`media-dot ${idx === currentMediaIndex ? 'active' : ''}`}
                      onClick={() => setCurrentMediaIndex(idx)}
                    >
                      {media.type === 'video' ? <Play size={10} /> : <ImageIcon size={10} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="modal-content">
              <div className="modal-header">
                <span className="modal-state">
                  <MapPin size={16} />
                  {stateNames[selectedAction.state]} • {formatMonth(selectedAction.month)}
                </span>
                <h2>{selectedAction.title}</h2>
                <p className="modal-subtitle">{selectedAction.subtitle}</p>
              </div>

              <div className="modal-info">
                <div className="info-item">
                  <MapPin size={18} />
                  <span>{selectedAction.location}</span>
                </div>
                <div className="info-item">
                  <Users size={18} />
                  <span>{selectedAction.beneficiaries}</span>
                </div>
              </div>

              <div className="modal-description">
                {selectedAction.description.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>

              <div className="modal-tags">
                {selectedAction.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapaAcoes;
